/**
 * useSimpleCycleConnector — Manages cycle boundary decorations on the overlay pane.
 *
 * Features:
 * - One rounded boundary per cycle, wrapping only that cycle's member cells
 * - Bounds derived from live cell states, so they track node moves and zoom/pan
 * - Clamped to the parent group's inner content area — never overflows the group
 * - "↻ ×N" iteration badge rendered inside the boundary's padding band
 * - Collapsed groups keep the compact chip on the swimlane header
 *
 * Redraws triggered by:
 * - View SCALE/TRANSLATE events (zoom/pan)
 * - View CHANGE events (node move/resize, group layout or fold)
 * - graphData changes (link/merge/reorganize)
 */

import { useCallback, useEffect } from 'react';
import { InternalEvent } from '@maxgraph/core';
import type { Cell, Graph, GraphView } from '@maxgraph/core';
import { useGraphEditorStore } from '@/graph-store';
import { resolveCycleAnchors } from '@/graph/domain/cycleAnchors';
import { CYCLE_COLORS } from '@/graph/cycleColors';
import {
  renderCycleBoundary,
  renderCollapsedCycleChip,
  computeCycleBoundaryBounds,
  cycleBadgeOverhang,
  clearAllConnectors,
  type CycleBoundaryDef,
  type CollapsedCycleChipDef,
  type BoundingBox,
} from '../decorations/SimpleCycleConnector';

/** Clearance between the cycle nodes and the boundary, in unscaled pixels. */
const BOUNDARY_PADDING = 16;
/** Extra clearance per cycle so stacked cycles read as concentric frames. */
const BOUNDARY_PADDING_STEP = 6;
/** Gap kept between the boundary and the parent group's own border. */
const GROUP_INNER_INSET = 3;
/** Swimlane header height fallback — mirrors GROUP_STYLE.startSize. */
const DEFAULT_GROUP_START_SIZE = 30;

/**
 * Index every edge cell by the operation it visually represents.
 *
 * Simple operations (TRANSFER/TRANSFORM/MEASURE) are collapsed into edges whose
 * value carries `operationNodeId`, so such a cycle member has no vertex of its
 * own — on screen it is the pair of materials the edge connects.
 */
function buildOperationEdgeIndex(graph: Graph): Map<string, Cell[]> {
  const index = new Map<string, Cell[]>();
  const cells = graph.getDataModel().cells ?? {};

  for (const cell of Object.values(cells)) {
    if (!cell?.isEdge()) continue;

    const value = cell.getValue() as { operationNodeId?: string } | null;
    const operationNodeId = value?.operationNodeId;
    if (!operationNodeId) continue;

    const edges = index.get(operationNodeId);
    if (edges) {
      edges.push(cell);
    } else {
      index.set(operationNodeId, [cell]);
    }
  }

  return index;
}

/**
 * Resolve cycle member IDs to the vertices that actually render them.
 * Vertices (materials, COMPLEX operations) map to themselves; edge-rendered
 * operations map to the two materials they connect.
 */
function resolveCycleVertices(
  graph: Graph,
  memberIds: string[],
  operationEdges: Map<string, Cell[]>,
): Cell[] {
  const model = graph.getDataModel();
  const vertices = new Map<string, Cell>();

  const addVertex = (cell: Cell | null) => {
    const id = cell?.getId();
    if (cell && id && cell.isVertex()) {
      vertices.set(id, cell);
    }
  };

  const addEdgeTerminals = (edge: Cell) => {
    addVertex(edge.getTerminal(true));
    addVertex(edge.getTerminal(false));
  };

  for (const memberId of memberIds) {
    const cell = model.getCell(memberId);

    if (cell?.isVertex()) {
      addVertex(cell);
      continue;
    }

    if (cell?.isEdge()) {
      addEdgeTerminals(cell);
      continue;
    }

    for (const edge of operationEdges.get(memberId) ?? []) {
      addEdgeTerminals(edge);
    }
  }

  return [...vertices.values()];
}

/**
 * Union of the on-screen bounds of the given cells.
 */
function computeBoundingBox(view: GraphView, cells: Cell[]): BoundingBox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const cell of cells) {
    const state = view.getState(cell);
    if (!state) continue;

    minX = Math.min(minX, state.x);
    minY = Math.min(minY, state.y);
    maxX = Math.max(maxX, state.x + state.width);
    maxY = Math.max(maxY, state.y + state.height);
  }

  if (minX === Infinity) return null;

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Inner content area of a group swimlane: inside its border and below its header.
 */
function computeGroupInnerBounds(
  view: GraphView,
  groupCell: Cell | null,
  scale: number,
): BoundingBox | null {
  if (!groupCell) return null;

  const state = view.getState(groupCell);
  if (!state) return null;

  const rawStartSize = state.style?.startSize;
  const startSize =
    (typeof rawStartSize === 'number' && Number.isFinite(rawStartSize)
      ? rawStartSize
      : DEFAULT_GROUP_START_SIZE) * scale;
  const inset = GROUP_INNER_INSET * scale;

  const width = state.width - inset * 2;
  const height = state.height - startSize - inset * 2;
  if (width <= 0 || height <= 0) return null;

  return {
    x: state.x + inset,
    y: state.y + startSize + inset,
    width,
    height,
  };
}

/**
 * Resolve the swimlane cell for a group.
 *
 * Prefers the id (groups are created with the store's own id), and falls back
 * to walking up from the member cells — so a renamed or re-created cell still
 * yields the swimlane that visually encloses the nodes rather than no clamp
 * at all.
 */
function resolveGroupCell(
  graph: Graph,
  groupId: string,
  memberCells: Cell[],
): Cell | null {
  const byId = graph.getDataModel().getCell(groupId);
  if (isGroupCell(byId)) return byId;

  for (const cell of memberCells) {
    let p = cell.getParent() as Cell | null;
    while (p !== null) {
      if (isGroupCell(p)) return p;
      p = (p as Cell).getParent() as Cell | null;
    }
  }
  return null;
}

function isGroupCell(cell: Cell | null): cell is Cell {
  const value = cell?.getValue() as { kind?: string } | null;
  return value?.kind === 'group';
}

/**
 * Keep only the cells that actually live inside the given group.
 *
 * Cycle membership is persisted, so a stale id can still resolve to a cell that
 * has since moved elsewhere. Anchoring the boundary to such a cell would drag it
 * away from the cycle it labels, so those cells are dropped here. Purely a
 * rendering guard — the cycle's own membership data is left untouched.
 */
function keepInsideGroup(cells: Cell[], groupCell: Cell | null): Cell[] {
  if (!groupCell) return cells;

  return cells.filter((cell) => {
    for (let p = cell.getParent(); p; p = p.getParent()) {
      if (p === groupCell) return true;
    }
    return false;
  });
}

/**
 * Manages cycle boundary rendering inside maxGraph's overlay pane.
 * Must be called inside a mounted GraphEditorProvider context.
 */
export function useSimpleCycleConnector(graph: Graph | null) {
  const graphData = useGraphEditorStore((s) => s.graphData);

  /**
   * Redraw every cycle boundary from the current cell states.
   */
  const redrawConnectors = useCallback(() => {
    if (!graph) return;

    const view = graph.getView();
    // maxGraph's own pane — shares the cell shapes' coordinate system exactly.
    const pane = view.getOverlayPane();
    if (!pane) return;

    clearAllConnectors(pane);

    const scale = view.scale;
    const operationEdges = buildOperationEdgeIndex(graph);

    const boundaryDefs: CycleBoundaryDef[] = [];
    const chipDefs: CollapsedCycleChipDef[] = [];

    // Iterate over groups to find cycles
    for (const [groupId, group] of Object.entries(graphData.groups)) {
      if (!group.cycleRanges || group.cycleRanges.length === 0) {
        continue;
      }

      // Handle collapsed groups - render chip on header
      if (group.isCollapsed) {
        const groupCell = graph.getDataModel().getCell(groupId);
        if (groupCell && groupCell.getGeometry()) {
          const groupState = view.getState(groupCell);
          if (groupState) {
            const chipDef: CollapsedCycleChipDef = {
              groupId,
              groupX: groupState.x,
              groupY: groupState.y,
              groupW: groupState.width,
              scale,
              cycles: group.cycleRanges.map((c, idx) => ({
                label: `↻ ×${c.iterationCount}`,
                color: CYCLE_COLORS[idx % CYCLE_COLORS.length],
              })),
            };
            chipDefs.push(chipDef);
          }
        }
        continue;
      }

      // Handle expanded groups - wrap each cycle's cells in a boundary that
      // can never leave the group's own content area.
      for (let cycleIndex = 0; cycleIndex < group.cycleRanges.length; cycleIndex++) {
        const cycle = group.cycleRanges[cycleIndex];

        // Reuse the membership already computed for this cycle; fall back to the
        // step range it covers when membership has not been derived yet.
        const memberIds = cycle.memberCellIds?.length
          ? cycle.memberCellIds
          : group.stepIds.slice(cycle.startIndex, cycle.endIndex + 1);

        let memberCells = resolveCycleVertices(graph, memberIds, operationEdges);
        const groupCell = resolveGroupCell(graph, groupId, memberCells);
        memberCells = keepInsideGroup(memberCells, groupCell);

        if (memberCells.length === 0) {
          // Last resort: the archetype-aware entry/exit materials of the range.
          const anchors = resolveCycleAnchors(cycle, group, graphData);
          if (anchors) {
            memberCells = keepInsideGroup(
              resolveCycleVertices(
                graph,
                [anchors.entryMaterialId, anchors.exitMaterialId],
                operationEdges,
              ),
              groupCell,
            );
          }
        }

        const memberBounds = computeBoundingBox(view, memberCells);
        if (!memberBounds) {
          continue;
        }

        const groupInnerBounds = computeGroupInnerBounds(view, groupCell, scale);

        const padding =
          (BOUNDARY_PADDING + BOUNDARY_PADDING_STEP * cycleIndex) * scale;
        const bounds = computeCycleBoundaryBounds(
          memberBounds,
          padding,
          cycleBadgeOverhang(scale),
          groupInnerBounds,
        );
        if (!bounds) {
          continue;
        }

        boundaryDefs.push({
          id: cycle.id,
          label: `↻ ×${cycle.iterationCount}`,
          color: CYCLE_COLORS[cycleIndex % CYCLE_COLORS.length],
          bounds,
          scale,
        });
      }
    }

    // Render all boundaries
    for (const def of boundaryDefs) {
      pane.appendChild(renderCycleBoundary(def));
    }

    // Render all chips for collapsed groups
    for (const chipDef of chipDefs) {
      const svg = renderCollapsedCycleChip(chipDef);
      pane.appendChild(svg);
    }
  }, [graph, graphData]);

  /**
   * Listen to graph view events (zoom, pan, and any re-validation caused by
   * node moves / resizes or a group layout change). Redraws are coalesced into
   * one animation frame so dragging stays smooth.
   */
  useEffect(() => {
    if (!graph) return;

    let frame = 0;
    const scheduleRedraw = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        redrawConnectors();
      });
    };

    const view = graph.getView();
    view.addListener('scale', scheduleRedraw);
    view.addListener('translate', scheduleRedraw);
    view.addListener('scaleAndTranslate', scheduleRedraw);
    view.addListener(InternalEvent.CHANGE, scheduleRedraw);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      view.removeListener(scheduleRedraw);
    };
  }, [graph, redrawConnectors]);

  /**
   * Redraw when graphData changes (handles link/merge/reorganize operations).
   */
  useEffect(() => {
    redrawConnectors();
  }, [graphData, redrawConnectors]);

  return { redrawConnectors };
}
